import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import type { ClientContact } from '@/shared/types/client';
import { maskPhone, maskPhoneFromStored } from '@/shared/lib/input-masks';
import { MaskedInput } from '@/shared/components/masked-input';
import { isValidEmail } from '@/features/nps/public-survey/partition-questions';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { CreatableCombobox } from '@/shared/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useCatalog, useCreateCatalogItem } from '@/features/catalog/api/use-catalog';

interface ClientContactsSectionProps {
  contacts: ClientContact[];
  onChange: (contacts: ClientContact[]) => void;
}

const EMPTY_CONTACT: ClientContact = { name: '', phone: '', email: '', role: '' };

export function ClientContactsSection({ contacts, onChange }: ClientContactsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<ClientContact>(EMPTY_CONTACT);
  const [error, setError] = useState<string | null>(null);

  const { data: cargos = [], isLoading: cargosLoading } = useCatalog('cargo');
  const createCargo = useCreateCatalogItem('cargo');
  const cargoOptions = cargos.filter((c) => c.active).map((c) => c.name);

  const openCreate = () => {
    setEditingIndex(null);
    setDraft({ ...EMPTY_CONTACT });
    setError(null);
    setDialogOpen(true);
  };

  const openEdit = (index: number) => {
    setEditingIndex(index);
    setDraft({
      ...contacts[index],
      phone: maskPhoneFromStored(contacts[index].phone),
    });
    setError(null);
    setDialogOpen(true);
  };

  const removeContact = (index: number) => {
    onChange(contacts.filter((_, i) => i !== index));
  };

  const saveContact = () => {
    if (!draft.name.trim()) {
      setError('Informe o nome do contato.');
      return;
    }
    if (draft.email && !isValidEmail(draft.email)) {
      setError('E-mail inválido.');
      return;
    }
    const next = [...contacts];
    if (editingIndex === null) {
      next.push({ ...draft, name: draft.name.trim() });
    } else {
      next[editingIndex] = { ...draft, name: draft.name.trim() };
    }
    onChange(next);
    setDialogOpen(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Contatos da empresa</h3>
          <p className="text-xs text-muted-foreground">Adicione quantos contatos forem necessários.</p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={openCreate}>
          <Plus className="size-4" /> Adicionar
        </Button>
      </div>

      {contacts.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
          Nenhum contato cadastrado.
        </p>
      ) : (
        <ul className="space-y-2">
          {contacts.map((contact, index) => (
            <li
              key={contact._id ?? `contact-${index}`}
              className="flex items-start justify-between gap-3 rounded-xl border border-border/50 bg-background/40 p-3"
            >
              <div className="min-w-0">
                <p className="font-medium">{contact.name}</p>
                <p className="text-xs text-muted-foreground">
                  {[contact.role, contact.phone, contact.email].filter(Boolean).join(' · ') || 'Sem detalhes'}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(index)} aria-label="Editar contato">
                  <Pencil className="size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  onClick={() => removeContact(index)}
                  aria-label="Excluir contato"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingIndex === null ? 'Novo contato' : 'Editar contato'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="contact-name">Nome</Label>
              <Input
                id="contact-name"
                value={draft.name}
                onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Celular</Label>
              <MaskedInput
                id="contact-phone"
                value={draft.phone ?? ''}
                onValueChange={(phone) => setDraft((prev) => ({ ...prev, phone }))}
                mask={maskPhone}
                type="tel"
                inputMode="tel"
                placeholder="(00) 00000-0000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">E-mail</Label>
              <Input
                id="contact-email"
                type="email"
                value={draft.email ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="contact-role">Cargo</Label>
              <CreatableCombobox
                value={draft.role ?? ''}
                onChange={(role) => setDraft((prev) => ({ ...prev, role }))}
                options={cargoOptions}
                loading={cargosLoading}
                placeholder="Selecione o cargo"
                emptyLabel="Nenhum cargo cadastrado."
                onCreate={async (label) => {
                  try {
                    await createCargo(label);
                  } catch {
                    toast.error('Falha ao criar cargo.');
                    throw new Error('Falha ao criar cargo.');
                  }
                }}
              />
            </div>
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={saveContact}>
              Salvar contato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
